import React from "react";
import type { DraftPreview, InspectorState } from "./websiteEditorInspectorTypes";
import type { JournalPost } from "./types";
import type { SectionCfg } from "./types";

export type WebsiteEditorInspectorContextValue = {
  enabled: boolean;
  locked: boolean;
  /** Dashboard Website shell: Link in Bio stack is editable in-canvas. */
  linkInBioEditable?: boolean;
  currentPage?: string;
  inspector: InspectorState | null;
  setInspector: (next: InspectorState | null) => void;
  /**
   * Force-close the edit drawer / selection chrome without the leave modal.
   * Used by mobile X (selection bar + sheet) so unselect always lands.
   */
  dismissSelection?: () => void;
  hovered: string | null;
  setHovered: (id: string | null) => void;
  /** Tour: keep this section's Edit button visible. */
  pinnedHover?: string | null;
  draftPreview: DraftPreview | null;
  /** Patch add-flow draft cfg (e.g. activeTab from ghost tab chips). */
  patchDraftCfg?: (patch: Partial<SectionCfg>) => void;
  openEditor: (id: "Site Header" | "Hero" | "Footer") => void;
  openAdd: (insertAt: number) => void;
  openComponent: (payload: {
    pageKey: string;
    sectionId: string;
    sectionType: string;
    label: string;
    idx: number;
  }) => void;
  /** Persist a new section order for the page (drag-and-drop). */
  setSectionOrder?: (pageKey: string, order: string[]) => void;
  /** Patch a journal card after approve / unpublish. */
  patchJournalPost?: (postId: string, patch: Partial<JournalPost>) => void;
  /**
   * Leave-guarded page switch (same as Pages rail / chrome ← Blog).
   * Prompts when Edit has uncommitted changes before navigating away.
   */
  requestSelectPage?: (pageKey: string) => void;
  /** Reorder the live collection strip while editing members / order. */
  reorderFeaturedCollection?: (
    memberIds: string[],
    collectionRowId?: string,
  ) => void;
  /**
   * Link in Bio feed ops (registered by WebsiteLinkInBioEditablePage) so Add
   * can paint the new row instantly without waiting on Stay refetch.
   */
  libFeedOpsRef?: React.MutableRefObject<LibFeedOps | null>;
};

export type LibFeedOps = {
  getShowing: () => any[];
  applyShowing: (next: any[]) => void;
  getArchive?: () => any[];
  /** Paint a new archive row instantly (create → Archive). */
  applyArchive?: (next: any[]) => void;
  /** Allow landingStay query to overwrite local feed again (after add refetch). */
  releaseSync?: () => void;
};

const WebsiteEditorInspectorContext =
  React.createContext<WebsiteEditorInspectorContextValue | null>(null);

export function WebsiteEditorInspectorProvider({
  value,
  children,
}: {
  value: WebsiteEditorInspectorContextValue;
  children: React.ReactNode;
}) {
  return (
    <WebsiteEditorInspectorContext.Provider value={value}>
      {children}
    </WebsiteEditorInspectorContext.Provider>
  );
}

export function useWebsiteEditorInspectorOptional(): WebsiteEditorInspectorContextValue | null {
  return React.useContext(WebsiteEditorInspectorContext);
}
